import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useQueries } from '@tanstack/react-query';
import { OMS_COLORS, tint } from '../omsDashTheme';
import { dashboardService } from '../../../../services/dashboardService';
import {
  UNMAPPED_LOCATION_ID,
  type LocationPerf,
  type PerformanceModel,
  type StatePerf,
} from '../utils/performanceModel';
import { formatNumber } from '../utils/formatNumber';
import { bandColor } from './HealthScore';
import ScoreGauge from './ScoreGauge';
import StatCount from './StatCount';

/** Aggregate card row — Level 1 renders states, Level 2 locations. */
type AggregateRow = StatePerf | LocationPerf;

interface Props {
  rows: AggregateRow[];
  model: PerformanceModel;
  deltaPhrase: string;
  compareIds: Set<string>;
  onToggleCompare: (id: string) => void;
  onDrill: (row: AggregateRow) => void;
}

/** Location ids behind a row — feeds the per-row trend sparkline query. */
function sparkLocationIds(row: AggregateRow, model: PerformanceModel): string[] {
  if (row.kind === 'state') return model.locationsOfState.get(row.name) ?? [];
  return row.id === UNMAPPED_LOCATION_ID ? [] : [row.id];
}

function metaLine(row: AggregateRow): string {
  const parts: string[] = [];
  if (row.kind === 'state') parts.push(`${formatNumber(row.locationCount)} location${row.locationCount === 1 ? '' : 's'}`);
  parts.push(`${formatNumber(row.projectCount)} project${row.projectCount === 1 ? '' : 's'}`);
  parts.push(`${formatNumber(row.engineerCount)} engineer${row.engineerCount === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/** Tiny inline completion-rate sparkline (8 weeks) — no chart lib needed. */
function Sparkline({ rates, name }: { rates: number[]; name: string }) {
  if (rates.length < 2) return null;
  const w = 88;
  const h = 24;
  const max = Math.max(...rates, 1);
  const points = rates
    .map((rate, i) => `${(i / (rates.length - 1)) * w},${h - (rate / max) * (h - 3) - 1.5}`)
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      role="img"
      aria-label={`${name} weekly completion-rate trend, latest ${Math.round(rates[rates.length - 1] ?? 0)}%`}
      className="shrink-0"
    >
      <polyline points={points} fill="none" stroke="#C9A961" strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The one-glance answer to "is every site doing well?" — entities ranked
 * best → needs-attention with their scores inline.
 */
function RankedStrip({ rows, onDrill }: { rows: AggregateRow[]; onDrill: (row: AggregateRow) => void }) {
  const ranked = [...rows].filter((r) => r.score).sort((a, b) => b.score!.score - a.score!.score);
  if (ranked.length < 2) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-[rgba(11,59,46,0.14)] px-2.5 py-2" style={{ backgroundColor: '#F8F2E2' }}>
      <span className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: '#1F5A47' }}>
        Best
      </span>
      {ranked.map((row, i) => (
        <span key={row.id} className="flex items-center gap-1.5">
          {i > 0 && <span aria-hidden style={{ color: 'rgba(11,59,46,0.25)' }}>›</span>}
          <button
            type="button"
            onClick={() => onDrill(row)}
            title={`Health score ${row.score!.score}/100`}
            className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold transition hover:bg-[rgba(11,59,46,0.06)]"
            style={{ color: OMS_COLORS.tealDark }}
          >
            {/* Band dot, not a numeral — scores only ever render as gauges. */}
            <span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: bandColor(row.score).fill }} />
            {row.name}
          </button>
        </span>
      ))}
      <span className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: '#9F1239' }}>
        Needs attention
      </span>
    </div>
  );
}

/**
 * Explorer aggregate levels — one engraved plate per state (Level 1) or
 * location (Level 2). The plate's title block carries the shared layoutId
 * that morphs into the next level's header: the card you click becomes
 * the header you land on.
 */
export default function LocationCards({ rows, model, deltaPhrase, compareIds, onToggleCompare, onDrill }: Props) {
  const sparkQueries = useQueries({
    queries: rows.map((row) => {
      const locationIds = sparkLocationIds(row, model);
      return {
        queryKey: ['oms-agg-spark', row.kind, row.id, locationIds.join(',')],
        queryFn: () => dashboardService.getWeeklyProgress({ locationIds, weeks: 8 }),
        staleTime: 10 * 60 * 1000,
        enabled: locationIds.length > 0,
      };
    }),
  });
  const sparkByRow = useMemo(() => {
    const map = new Map<string, number[]>();
    rows.forEach((row, i) => {
      const series = sparkQueries[i]?.data?.series;
      if (series && series.length >= 2) map.set(row.id, series.map((p) => p.completionRate));
    });
    return map;
  }, [rows, sparkQueries]);

  return (
    <div className="space-y-3">
      <RankedStrip rows={rows} onDrill={onDrill} />
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((row, index) => {
          const completionPct = row.scheduled > 0 ? row.completed / row.scheduled : null;
          const selected = compareIds.has(row.id);
          return (
            <motion.li
              key={row.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.06, 0.36), duration: 0.35 }}
            >
              <div
                className="group relative h-full rounded-2xl border border-[rgba(11,59,46,0.24)] p-4 transition-shadow hover:shadow-[0_14px_34px_-18px_rgba(42,31,23,0.4)]"
                style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}
              >
                <button
                  type="button"
                  data-perf-row
                  onClick={() => onDrill(row)}
                  aria-label={`Open ${row.name} — ${row.projectCount} projects`}
                  className="w-full rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-[rgba(51,146,116,0.5)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <motion.div layoutId={`plate-${row.id}`} className="min-w-0">
                      <h3 className="truncate text-lg font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                        {row.name}
                      </h3>
                      <p className="text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                        {metaLine(row)}
                      </p>
                    </motion.div>
                    <ScoreGauge
                      score={row.score}
                      name={row.name}
                      size={56}
                      delta={row.delta}
                      deltaPhrase={deltaPhrase}
                      align="end"
                      showExplainer={false}
                    />
                  </div>

                  {/* Divider: gauge above is the score block, counts live below it. */}
                  <div className="mt-3 flex items-end justify-between gap-2 border-t border-[rgba(11,59,46,0.10)] pt-2.5">
                    <div className="space-y-1.5">
                      <StatCount
                        label="Completed"
                        value={row.completed}
                        of={row.scheduled}
                        noun="tasks"
                        size="sm"
                        valueText={completionPct === null ? '—' : undefined}
                      />
                      <StatCount
                        label="Overdue"
                        value={row.overdue}
                        of={row.open}
                        noun="open tasks"
                        size="sm"
                        ink={row.overdue > 0 ? '#9F1239' : OMS_COLORS.tealDark}
                      />
                    </div>
                    <Sparkline rates={sparkByRow.get(row.id) ?? []} name={row.name} />
                  </div>
                </button>

                <label
                  className={`absolute right-3 top-3 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                    selected ? 'flex' : 'hidden group-focus-within:flex group-hover:flex'
                  }`}
                  style={{ color: OMS_COLORS.champagneDeep, backgroundColor: selected ? tint('#C9A961', 0.2) : 'transparent' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleCompare(row.id)}
                    aria-label={`Compare ${row.name}`}
                    className="h-3 w-3 accent-[#264f3c]"
                  />
                  Compare
                </label>
              </div>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
